<template>
  <div class="section-editor-container">
    <div
      ref="editorRef"
      :class="['section-editor', isPreviewMode && 'preview-mode', readOnly && 'read-only']"
      @click="handleDisableLinkClick"
    >
      <template v-if="editor && !isPreviewMode">
        <div class="editor-toolbar">
          <div
            v-for="(group, index) in toolbarItems"
            :key="index"
            class="toolbar-group"
          >
            <AntButton
              v-for="item in group"
              :id="item.name"
              :key="item.name"
              :title="item.title"
              :type="item.isActive ? 'primary' : 'text'"
              :disabled="item.disabled"
              @click="item.onClick"
            >
              <component :is="item.icon" />
            </AntButton>
          </div>

          <div
            v-show="headingMenuVisible"
            class="context-menu heading-menu"
            @click.stop
          >
            <div class="context-menu-group">
              <div
                class="context-menu-item"
                :class="{ active: editor.isActive('heading', { level: 3 }) }"
                @click="handleHeadingClick(3)"
              >
                <h3>Heading 3</h3>
              </div>
              <div
                class="context-menu-item"
                :class="{ active: editor.isActive('heading', { level: 4 }) }"
                @click="handleHeadingClick(4)"
              >
                <h4>Heading 4</h4>
              </div>
            </div>
          </div>
        </div>
        <EditorContent
          class="section-editor-content"
          :editor="editor"
          @contextmenu="onContextMenu"
        />
      </template>
    <div v-else class="section-editor-content" @click="handleDisableLinkClick">
      <div v-if="contentHtml" v-html="contentHtml"></div>
      </div>
    </div>
    <div v-if="isDev" style="font-size: 11px; color: #bbb;">
      <div>html: {{ html }}</div>
      <div>content: {{ content }}</div>
      <!-- <div>isPreviewMode: {{ isPreviewMode }}</div> -->
    </div>

    <div
      v-if="tableContextMenuState.visible"
      class="context-menu"
      :style="{ left: tableContextMenuState.x + 'px', top: tableContextMenuState.y + 'px' }"
      @click.stop
    >
      <div class="context-menu-group">
        <div class="context-menu-title">Cell Actions</div>
        <div
          v-show="editor?.can().mergeCells()"
          class="context-menu-item"
          @click="handleMergeCells"
        >
          <MergeCellsIcon /> Merge Cells
        </div>
        <div
          v-show="editor?.can().splitCell()"
          class="context-menu-item"
          @click="handleSplitCells"
        >
          <SplitCellsIcon /> Split Cells
        </div>
      </div>
      <div class="context-menu-group">
      <div class="context-menu-title">Column Actions</div>
        <div
          v-show="editor?.can().addColumnBefore()"
          class="context-menu-item"
          @click="editor.chain().focus().addColumnBefore().run()"
        >
          <InsertColumnLeftIcon /> Insert Column Left
        </div>
        <div
          v-show="editor?.can().addColumnAfter()"
          class="context-menu-item"
          @click="handleAddColumnAfter"
        >
          <InsertColumnRightIcon /> Insert Column Right
        </div>
        <div
          v-show="editor?.can().deleteColumn()"
          class="context-menu-item"
          @click="handleDeleteColumn"
        >
          <DeleteColumnIcon /> Delete Column
        </div>
      </div>
      <div class="context-menu-group">
      <div class="context-menu-title">Row Actions</div>
        <div
          v-show="editor?.can().addRowBefore()"
          class="context-menu-item"
          @click="handleAddRowBefore"
        >
          <InsertRowAboveIcon /> Insert Row Above
        </div>
        <div
          v-show="editor?.can().addRowAfter()"
          class="context-menu-item"
          @click="handleAddRowAfter"
        >
          <InsertRowBelowIcon /> Insert Row Below
        </div>
        <div
          v-show="editor?.can().deleteRow()"
          class="context-menu-item"
          @click="handleDeleteRow"
        >
          <DeleteRowIcon /> Delete Row
        </div>
      </div>
      <div class="context-menu-group">
        <div class="context-menu-title">Table Actions</div>
        <div
          v-show="editor?.can().deleteTable()"
          class="context-menu-item"
          @click="handleDeleteTable"
        >
          <TableIcon /> Delete Table
        </div>
      </div>
    </div>

    <AntModal
      v-model:visible="linkState.modalVisible"
      width="600px"
      centered
      title="Insert Link"
      :getContainer="getModalContainer"
      :okButtonProps="{ disabled: !linkState[linkState.activeTab].url || !linkState[linkState.activeTab].text }"
      @cancel="closeLinkModal"
      @ok="insertLink"
    >
      <div class="modal-container">
        <div v-if="isDev" style="font-size: 12px; display: flex; gap: 10px;">
          <pre style="font-size: 10px; width: 50%; max-height: 150px; overflow-y: auto;">linkState: {{ JSON.stringify(linkState, null, 2) }}</pre>
          <div style="width: 50%;max-height: 150px; overflow-y: auto">
            <div><b>customizedVersionSelectedDocId:</b> {{ customizedVersionSelectedDocId }}</div>
            <div><b>expandedKeys:</b> {{ expandedKeys }}</div>
            <div><b>selectedKeys:</b> {{ selectedKeys }}</div>
            <div><b>linkTreeDocId:</b> {{ linkTreeDocId }}</div>
          </div>
        </div>

        <AntTabs v-model:activeKey="linkState.activeTab">
          <AntTabPane
            key="internal"
            class="modal-tab-pane"
            tab="Internal Section Link"
          >
            <!-- <div class="ant-row ant-form-item">
            <div class="ant-col ant-col-6 ant-form-item-label ant-form-item-label-left">
              <label>Link Title Language</label>
            </div>
            <div class="ant-col ant-col-18 ant-form-item-control">

            </div>
          </div>-->
            <div class="form-item">
              <label>Link Title Language: </label>
              <AntRadioGroup v-model:value="linkState.internal.lang">
                <AntRadio value="en">English</AntRadio>
                <AntRadio value="cn">Chinese</AntRadio>
                <AntRadio value="encn">English + Chinese</AntRadio>
              </AntRadioGroup>
            </div>
            <AntInputSearch
              v-model:value="treeSearchState.value"
              placeholder="Search"
              style="margin: 8px 0"
              @search="onSearchTree"
              allowClear
            />
            <div class="modal-scroll-container">
              <AntTree
                v-model:expandedKeys="expandedKeys"
                v-model:selectedKeys="selectedKeys"
                :treeData="displayedTreeData"
                :fieldNames="{ key: 'sectionId', title: 'treeTitleWithRemark', children: 'treeChildren' }"
                defaultExpandAll
                @select="onSelectTree"
              >
                <template #title="{ sectionId, treeTitleWithRemark }">
                  <div :id="`section-${sectionId}`">
                    <span v-if="isDev" style="color:BurlyWood;">{{ sectionId }} </span> {{ treeTitleWithRemark }}
                  </div>
                </template>
              </AntTree>
            </div>
          </AntTabPane>
          <AntTabPane
            key="external"
            class="modal-tab-pane"
            tab="External Link"
          >
            <div class="modal-scroll-container">
              <div class="form-item">
                <AntInput
                  v-model:value="linkState[linkState.activeTab].url"
                  placeholder="Link URL"
                  allowClear
                />
              </div>
              <div class="form-item">
                <AntInput
                  v-model:value="linkState[linkState.activeTab].text"
                  placeholder="Link Text"
                  allowClear
                />
              </div>
              <!-- <div class="form-item">
                <label>Open in New Tab: </label>
                <AntSwitch v-model:checked="linkState.external.openInNewTab" />
              </div> -->
            </div>
          </AntTabPane>
        </AntTabs>
      </div>
    </AntModal>

    <AntModal
      v-model:visible="imageState.modalVisible"
      title="Insert Image"
      width="800px"
      centered
      :getContainer="getModalContainer"
      @cancel="closeImageModal"
      @ok="insertImage"
    >
      <div class="modal-container">
        <DevDebugDataBlock
          v-if="isDev"
          title="imageState"
          :data="imageState"
          width="100%"
          maxHeight="150px"
        />
        <AntTabs v-model:activeKey="imageState.activeTab">
          <AntTabPane
            key="internal"
            class="modal-tab-pane"
            tab="Uploaded Images"
          >
            <div class="modal-scroll-container">
              <AntAlert
                type="warning"
                message="以下為 API 編輯器系統圖庫集，若移除會導致使用該圖的 API 文件無法顯示圖片"
                banner
                style="margin-bottom: 16px;"
              />

              <AntRow
                justify="space-between"
                class="image-search-row"
              >
                <AntCol :span="16">
                  <AntRow :gutter="16">
                    <AntCol :span="18">
                      <AntInput
                        v-model:value="imageSearchText"
                        placeholder="Search"
                        allowClear
                        @pressEnter="onSearchImages"
                      />
                    </AntCol>
                    <AntCol :span="6">
                      <AntButton
                        type="primary"
                        @click="onSearchImages"
                      >
                        <template #icon>
                          <SearchIcon />
                        </template>
                        Search
                      </AntButton>
                    </AntCol>
                  </AntRow>
                </AntCol>

                <AntUpload
                  :beforeUpload="beforeUpload"
                  :customRequest="onUpload"
                  :showUploadList="false"
                >
                  <AntButton
                    type="primary"
                    :loading="imageState.uploadLoading"
                  >
                    <template #icon>
                      <AddIcon />
                    </template>
                    Upload
                  </AntButton>
                </AntUpload>
              </AntRow>

              <AntTable
                class="image-management-table"
                :columns="imageTableColumns"
                :dataSource="displayedImagesData"
                :pagination="imagePagination"
                :showHeader="false"
                :rowClassName="getImageRowClassName"
                :customRow="getImageTableCustomRow"
                :scroll="{ y: 450 }"
                @change="onImageTableChange"
              >
                <template #bodyCell="{ column, record }">
                  <template v-if="column.dataIndex === 'preview'">
                    <div
                      class="image-preview-wrap"
                      @click.stop
                    >
                      <AntImage
                        :src="getImageDisplayUrl(record)"
                        :alt="getImageAltText(record)"
                        :width="48"
                        :height="48"
                        :preview="{
                          src: getImageDisplayUrl(record),
                          getContainer: getModalContainer,
                        }"
                      />
                    </div>
                  </template>

                  <template v-if="column.dataIndex === 'altText'">
                    <AntInput
                      v-if="editableImageData[record.id]"
                      v-model:value="editableImageData[record.id].altText"
                      placeholder="Alt Text"
                      allowClear
                      @click.stop
                    />
                    <div v-else>
                      {{ getImageAltText(record) }}
                    </div>
                  </template>

                  <template v-if="column.dataIndex === 'action'">
                    <div
                      class="image-table-actions"
                      @click.stop
                    >
                      <span v-if="editableImageData[record.id]">
                        <SaveIcon
                          title="Save"
                          @click="onSaveImageAlt(record)"
                        />
                        <CancelIcon
                          title="Cancel"
                          @click="onCancelImageEdit(record)"
                        />
                      </span>
                      <span v-else>
                        <EditIcon
                          title="Edit"
                          @click="onEditImage(record)"
                        />
                        <PreviewIcon
                          title="Preview"
                          @click="onPreviewImage(getImageDisplayUrl(record))"
                        />
                        <AntPopconfirm
                          title="刪除後可能會影響有使用到此圖的 API 文件無法顯示，確認刪除？"
                          okText="確認"
                          cancelText="取消"
                          trigger="click"
                          placement="bottom"
                          :overlayStyle="{ width: '300px' }"
                          :getPopupContainer="getPopconfirmContainer"
                          @confirm="onDeleteImage(record.id)"
                        >
                          <DeleteIcon title="Delete" />
                        </AntPopconfirm>
                      </span>
                    </div>
                  </template>
                </template>
              </AntTable>

              <AntImage
                v-if="imageState.previewVisible"
                :style="{ display: 'none' }"
                :preview="{
                  visible: imageState.previewVisible,
                  src: imageState.previewUrl,
                  onVisibleChange: (visible) => imageState.previewVisible = visible
                }"
                :getContainer="getModalContainer"
              />
            </div>
          </AntTabPane>
          <AntTabPane
            key="external"
            class="modal-tab-pane"
            tab="External Image Link"
          >
            <div class="form-item">
              <label>Image URL: </label>
              <AntInput
                v-model:value="imageState[imageState.activeTab].imageUrl"
                placeholder="Image URL"
                allowClear
              />
            </div>
            <div class="form-item">
              <label>Image Text: </label>
              <AntInput
                v-model:value="imageState[imageState.activeTab].altText"
                placeholder="Image Text"
                allowClear
              />
            </div>
          </AntTabPane>
        </AntTabs>
      </div>
    </AntModal>
  </div>
</template>

<script>
import {
  ref,
  reactive,
  inject,
  watch,
  // watchEffect,
  computed,
  onMounted,
  onBeforeUnmount,
  nextTick,
} from 'vue'

// import pangu from 'pangu'

import { cloneDeep, throttle } from 'lodash'
import moment from 'moment'

import { useEditor, EditorContent } from '@tiptap/vue-3'
import { Extension } from '@tiptap/core'
import StarterKit from '@tiptap/starter-kit'
import Table from '@tiptap/extension-table'
import TableRow from '@tiptap/extension-table-row'
import TableCell from '@tiptap/extension-table-cell'
import TableHeader from '@tiptap/extension-table-header'
import Link from '@tiptap/extension-link'
import Image from '@tiptap/extension-image'
import { Color } from '@tiptap/extension-color'
import TextStyle from '@tiptap/extension-text-style'
import { Markdown } from 'tiptap-markdown'

import { TextSelection, Plugin } from 'prosemirror-state'
import { DOMParser as ProseMirrorDOMParser } from 'prosemirror-model'

import {
  Tree as AntTree,
  Input as AntInput,
  Modal as AntModal,
  Tabs as AntTabs,
  Upload as AntUpload,
  Image as AntImage,
  Button as AntButton,
  Alert as AntAlert,
  Radio as AntRadio,
  Switch as AntSwitch,
  Popconfirm as AntPopconfirm,
  message as antMessage,
  Row as AntRow,
  Col as AntCol,
  Table as AntTable,
} from 'ant-design-vue'

import {
  PlusOutlined,
  DeleteOutlined,
  EyeOutlined,
  LoadingOutlined,
  SearchOutlined,
  EditOutlined,
  CheckOutlined,
  CloseOutlined,
  BoldOutlined,
  ItalicOutlined,
  CodeOutlined,
  FontColorsOutlined,
  StrikethroughOutlined,
  OrderedListOutlined,
  UnorderedListOutlined,
  TableOutlined,
  MergeCellsOutlined,
  SplitCellsOutlined,
  InsertRowAboveOutlined,
  InsertRowBelowOutlined,
  InsertRowLeftOutlined,
  InsertRowRightOutlined,
  DeleteRowOutlined,
  DeleteColumnOutlined,
  LinkOutlined,
  UndoOutlined,
  RedoOutlined,
  FontSizeOutlined,
  PictureOutlined,
} from '@ant-design/icons-vue'

import {
  deleteManagementItem,
  updateManagementItem,
  uploadImage,
  deleteImages,
} from '@/api/apiDocV3'

import DevDebugDataBlock from '@/components/ApiDoc/shared/DevDebugDataBlock.vue'

import { titleCase } from '@/utils'
import { transformToNested, findTreeAndKeys } from '../lib/libsV3'

const CustomTable = Table.extend({
  renderHTML({ HTMLAttributes }) {
    const {
      style, colspan, rowspan, ...rest // eslint-disable-line no-unused-vars
    } = HTMLAttributes
    return ['table', rest, 0]
  },
})

const CustomTableCell = TableCell.extend({
  renderHTML({ HTMLAttributes }) {
    const {
      style, colspan, rowspan, ...rest // eslint-disable-line no-unused-vars
    } = HTMLAttributes
    const attributes = { ...rest }
    if (colspan && colspan !== 1) {
      attributes.colspan = colspan
    }
    return ['td', attributes, 0]
  },
})

const CustomTableHeader = TableHeader.extend({
  renderHTML({ HTMLAttributes }) {
    const {
      style, colspan, rowspan, ...rest // eslint-disable-line no-unused-vars
    } = HTMLAttributes
    const attributes = { ...rest }
    if (colspan && colspan !== 1) {
      attributes.colspan = colspan
    }
    return ['th', attributes, 0]
  },
})

// 自定義 Link 擴展，確保輸出的 HTML 不包含 target 屬性
// const CustomLink = Link.extend({
//   renderHTML({ HTMLAttributes }) {
//     const { target, ...rest } = HTMLAttributes
//     return ['a', rest, 0]
//   },
// })

const RemoveSpanOnPaste = Extension.create({
  name: 'removeSpanOnPaste',

  addProseMirrorPlugins() {
    return [
      new Plugin({
        props: {
          handlePaste(view, event) {
            const html = event.clipboardData?.getData('text/html')
            const text = event.clipboardData?.getData('text/plain')

            if (html) {
              // 移除 <span> 但保留其內容
              const cleanedHtml = html.replace(/<\/?span[^>]*>/gi, '')

              // 將 HTML 字串轉換為 DOM
              const parser = new window.DOMParser()
              const doc = parser.parseFromString(cleanedHtml, 'text/html')

              // 使用 ProseMirror DOMParser 解析 DOM 為 Fragment
              const slice = ProseMirrorDOMParser
                .fromSchema(view.state.schema)
                .parseSlice(doc.body)

              const transaction = view.state.tr.replaceSelection(slice)
              view.dispatch(transaction)
              return true
            }

            if (text) {
              const transaction = view.state.tr.insertText(text)
              view.dispatch(transaction)
              return true
            }

            return false
          },
        },
      }),
    ]
  },
})
// const CleanTextStyle = TextStyle.extend({
//   parseHTML() {
//     return [
//       {
//         tag: 'span',
//         getAttrs: () => ({}), // 只允許 span，但忽略其所有屬性
//       },
//     ]
//   },
// })

export default {
  name: 'SectionEditor',
  components: {
    EditorContent,
    DevDebugDataBlock,
    AntTree,
    AntModal,
    AntTabs,
    AntTabPane: AntTabs.TabPane,
    AntInput,
    AntInputSearch: AntInput.Search,
    AntUpload,
    AntImage,
    AntButton,
    AntAlert,
    AntRow,
    AntCol,
    AntTable,
    AntRadio,
    AntRadioGroup: AntRadio.Group,
    AntSwitch,
    AntPopconfirm,
    AddIcon: PlusOutlined,
    DeleteIcon: DeleteOutlined,
    PreviewIcon: EyeOutlined,
    LoadingIcon: LoadingOutlined,
    SearchIcon: SearchOutlined,
    EditIcon: EditOutlined,
    SaveIcon: CheckOutlined,
    CancelIcon: CloseOutlined,
    HeadingIcon: FontSizeOutlined,
    BoldIcon: BoldOutlined,
    ItalicIcon: ItalicOutlined,
    StrikethroughIcon: StrikethroughOutlined,
    ColorIcon: FontColorsOutlined,
    CodeIcon: CodeOutlined,
    ListIcon: UnorderedListOutlined,
    OrderedListIcon: OrderedListOutlined,
    TableIcon: TableOutlined,
    LinkIcon: LinkOutlined,
    UndoIcon: UndoOutlined,
    RedoIcon: RedoOutlined,
    ImageIcon: PictureOutlined,
    MergeCellsIcon: MergeCellsOutlined,
    SplitCellsIcon: SplitCellsOutlined,
    InsertRowAboveIcon: InsertRowAboveOutlined,
    InsertRowBelowIcon: InsertRowBelowOutlined,
    InsertColumnLeftIcon: InsertRowLeftOutlined,
    InsertColumnRightIcon: InsertRowRightOutlined,
    DeleteRowIcon: DeleteRowOutlined,
    DeleteColumnIcon: DeleteColumnOutlined,
  },
  inheritAttrs: false,
  props: {
    html: {
      type: Boolean,
      default: false,
    },
    height: {
      type: String,
      default: 'auto',
    },
    sectionsDataByDocId: Object,
    imageManagementData: Array,
    editorContent: {
      type: String,
      default: '',
    },
    onChange: Function,
    onBlur: Function,
    previewMode: {
      type: Boolean,
      default: false,
    },
    /** 鎖定預覽，不可點擊進入編輯（Activity Log 更版說明） */
    readOnly: {
      type: Boolean,
      default: false,
    },
    removedToolbarItems: {
      type: Array,
      default: () => [],
    },
    customizedVersionSelectedDocId: String,
  },
  setup(props, { expose }) {
    const isDev = inject('isDev')

    const isPreviewMode = ref(props.previewMode || props.readOnly)
    const editorRef = ref()
    const content = ref(props.editorContent)
    // .replace(/([a-zA-Z])([\u4e00-\u9fff])/g, '$1 $2')
    // .replace(/([\u4e00-\u9fff])([a-zA-Z0-9])/g, '$1 $2')
    // .replace(/,([\u4e00-\u9fff])/g, '，$1')
    // .replace(/,([\u4e00-\u9fff])/g, '，$1')
    // .replace(/(\S)"(\S+.*\S)"(\S)/g, '$1 "$2" $3')
    // .replace(/(\S)-(\S)/g, '$1 - $2')
    // .replace(/,([a-zA-Z0-9])/g, ', $1')
    // .replace(/([a-zA-Z0-9]):([a-zA-Z0-9])/g, '$1: $2')
    // .replace(/([\u4e00-\u9fff]):\s?([a-zA-Z0-9]|[\u4e00-\u9fff])/g, '$1：$2')
    // .replace(/(\d)\.([a-zA-Z]|[\u4e00-\u9fff])/g, '$1. $2')
    // .replace(/([a-zA-Z])：([a-zA-Z])/g, '$1: $2')
    // .replace(/\s\./g, '')
    // .replace(/([a-zA-Z0-9])\(([a-zA-Z0-9]|[\u4e00-\u9fff])/g, '$1 ($2')
    // .replace(/年 (\d+)月 (\d+)日/g, '年$1月$2日')

    const tableContextMenuState = reactive({
      visible: false,
      x: 0,
      y: 0,
    })

    const defaultLinkState = {
      modalVisible: false,
      activeTab: 'internal',
      internal: {
        url: '',
        text: '',
        lang: 'en',
      },
      external: {
        url: '',
        text: '',
        openInNewTab: false,
      },
    }
    const linkState = reactive(cloneDeep(defaultLinkState))

    const linkTreeDocId = computed(() => props.customizedVersionSelectedDocId
      || Object.keys(props.sectionsDataByDocId || {}).find(k => k.endsWith('#master')))

    const treeData = computed(() => transformToNested({
      data: props.sectionsDataByDocId?.[linkTreeDocId.value] || [],
    }))
    const displayedTreeData = ref([])

    const expandedKeys = ref([])
    const selectedKeys = ref([])

    const treeSearchState = reactive({
      value: '',
      text: '',
      filtered: false,
    })

    watch(linkTreeDocId, () => {
      treeSearchState.value = ''
      treeSearchState.text = ''
      treeSearchState.filtered = false
      displayedTreeData.value = treeData.value
      expandedKeys.value = treeData.value.map(({ sectionId }) => sectionId)
    }, { immediate: true })

    const formatContent = (text) => {
      let formattedText = text.trim()
      formattedText = formattedText.replace(/&nbsp;/g, ' ') // 處理 &nbsp; 無法換行，在表格會太寬問題
        .replace(/&amp;amp;/g, '&amp;') // 處理插入 8.7. Supported Platform &amp;amp; API in AWC Platform 的問題
        .replace(/&amp;/g, '&')

      // formattedText = pangu.spacing(formattedText)
      // formattedText = formattedText.replace(/,([^\s])/g, ', $1') // 英文逗號後補空格
      // .replace(/:([^\s|<|\\|/])/g, ': $1') // 英文冒號後補空格
      // .replace(/([a-zA-Z0-9])\.([^\s|<|\\])/g, '$1. $2') // 英文句號後補空格
      // .replace(/([a-zA-Z0-9])\(/g, '$1 (') // 英文括號前補空格
      // .replace(/\)([a-zA-Z0-9])/g, ') $1') // 英文括號後補空格
      // .replace(/\)\s*\(/g, ') (') // 處理連續括號之間的空格

      return formattedText
    }

    const editor = useEditor({
      extensions: [
        StarterKit,
        Markdown.configure({
          breaks: true,
        }),
        TextStyle,
        Color,
        CustomTable,
        TableRow,
        CustomTableCell,
        CustomTableHeader,
        Link.configure({
          openOnClick: false,
          HTMLAttributes: {
            rel: null,
            target: null,
          },
        }),
        Image,
        RemoveSpanOnPaste,
      ],
      content: content.value,
      editorProps: {
        transformPastedText: (text) => {
          console.log('transformPastedText text', text)
          return text
        },
      },
      onUpdate: () => {
        throttledUpdate() // eslint-disable-line no-use-before-define
      },
      onBlur: () => {
        handleEditorBlur() // eslint-disable-line no-use-before-define
      },
    })

    const setContentAndRestoreCursor = (ctnt) => {
      if (!editor.value || editor.value.isDestroyed) return
      console.log('setContentAndRestoreCursor', { ctnt })

      const currentSelection = editor.value.state.selection
      const cursorPosition = currentSelection.from

      editor.value.commands.setContent(ctnt)

      // 在 setContent 後恢復游標位置
      nextTick(() => {
        try {
          const docLength = editor.value.state.doc.content.size
          const safePosition = Math.min(cursorPosition, docLength)

          editor.value.commands.setTextSelection(safePosition)
          editor.value.commands.focus()
        } catch (error) {
          console.warn('Failed to restore cursor position:', error)
          editor.value.commands.setTextSelection(editor.value.state.doc.content.size)
          editor.value.commands.focus()
        }
      })
    }

    /** 父層切換 General／子本時略過 blur／throttle 寫回，避免舊編輯器內容寫錯 scope */
    let suppressPushToParent = false

    const pushContentToParent = (formattedContent) => {
      if (suppressPushToParent) return
      if (formattedContent === props.editorContent) return
      props.onChange(formattedContent)
    }

    const throttledUpdate = throttle(() => {
      // 防止在 setContent 過程中觸發更新
      if (editor.value.isDestroyed) return

      const hasTable = editor.value.state.doc.content.content.some(node => node.type.name === 'table')
      let markdown = editor.value.storage.markdown.getMarkdown()

      // 處理 List of Platforms 中 - platform: BG - gameType: LIVE... 儲存後再打開會把 - gameType: LIVE 變成 ul，因此在 - 前面加上 escape
      if (/\\\n-/g.test(markdown)) {
        markdown = markdown.replace(/\\\n-/g, '\\\n\\-')
        console.log('editor blur markdown', { markdown })
      }

      if (!editor.value || editor.value.isDestroyed) return

      const newContent = props.html || hasTable ? editor.value.getHTML() : markdown
      const formattedContent = formatContent(newContent)

      if (content.value === formattedContent) return

      console.log('throttledUpdate', {
        'content.value': content.value,
        newContent,
        formattedContent,
        markdown,
        hasTable,
      })

      content.value = formattedContent
      pushContentToParent(formattedContent)
    }, 300)

    const handleEditorBlur = () => {
      throttledUpdate.flush()
      if (props.onBlur) props.onBlur(content.value)
    }

    watch(() => props.editorContent, (newVal, oldVal) => {
      throttledUpdate.cancel()
      if (newVal === content.value) return
      if (newVal === oldVal) return

      suppressPushToParent = true
      content.value = newVal
      setContentAndRestoreCursor(content.value)
      nextTick(() => {
        nextTick(() => {
          suppressPushToParent = false
        })
      })
    })

    const handleDisableLinkClick = (e) => {
      console.log('handleDisableLinkClick', e.target)
      if (e.target.tagName === 'A') e.preventDefault()
    }

    const contentHtml = computed(() => {
      if (!editor.value) return '<p></p>'
      const html = editor.value.getHTML()
      return html
    })

    // 搬移 Release Verison 用
    // onMounted(() => {
    //   if (props.html) {
    //     props.onChange(editor.value.getHTML())
    //   }
    // })

    // onMounted(() => {
    //   if (props.editorContent === props.editorContent.replace(/&nbsp;/g, ' ')) return
    //   props.onChange(props.editorContent.replace(/&nbsp;/g, ' '))
    // })

    expose({ editor })

    const onSearchTree = () => {
      treeSearchState.text = treeSearchState.value

      if (treeSearchState.value) {
        treeSearchState.filtered = true
        const findResult = findTreeAndKeys(treeData.value, treeSearchState.text)
        expandedKeys.value = [...new Set([...expandedKeys.value, ...findResult.keys])]
        displayedTreeData.value = findResult.nestedTree
      } else {
        treeSearchState.filtered = false
        displayedTreeData.value = treeData.value
        expandedKeys.value = treeData.value.map(({ sectionId }) => sectionId)
      }
    }

    const selectedTreeNode = ref(null)

    const setLinkTitleAndUrlByLang = (lang) => {
      if (!selectedTreeNode.value || !selectedKeys.value[0]) {
        linkState.internal.text = ''
        linkState.internal.url = ''
        return
      }

      if (lang === 'encn') {
        linkState.internal.text = `${selectedTreeNode.value.treeTitle}`
      } else {
        linkState.internal.text = `${selectedTreeNode.value[`tree${titleCase(lang)}Title`]}`
      }
      linkState.internal.url = `${selectedKeys.value[0]}_${lang}`
    }

    watch(() => linkState.internal.lang, setLinkTitleAndUrlByLang)

    const onSelectTree = ([selectedKey], { node }) => {
      console.log('onSelectTree selectedKey', selectedKey, node)

      selectedKeys.value = [selectedKey]
      selectedTreeNode.value = node
      setLinkTitleAndUrlByLang(linkState.internal.lang)
    }

    const closeLinkModal = () => {
      Object.assign(linkState, cloneDeep(defaultLinkState))
      selectedKeys.value = []
    }

    const imagesData = reactive(props.imageManagementData)

    const defaultImageState = {
      activeTab: 'internal',
      modalVisible: false,
      previewVisible: false,
      previewUrl: '',
      uploadLoading: false,
      internal: {
        imageUrl: '',
        altText: '',
      },
      external: {
        imageUrl: '',
        altText: '',
      },
    }

    const imageState = reactive({ ...defaultImageState })

    const getImageFileName = (item, id) => item?.fileName || id

    const getImageAltText = item => item?.altText || getImageFileName(item, item?.id) || ''
    const getImageDisplayUrl = item => item?.imageUrl || item?.s3Url

    const imageSearchText = ref('')
    const appliedImageSearchText = ref('')
    const editableImageData = reactive({})
    const imagePagination = ref({
      current: 1,
      pageSize: 50,
      pageSizeOptions: ['50', '100', '200'],
      showSizeChanger: true,
      size: 'small',
      showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
    })

    const imageTableColumns = [
      {
        title: 'Image',
        dataIndex: 'preview',
        width: '80px',
        className: 'image-preview-cell',
      },
      {
        title: 'Alt Text',
        dataIndex: 'altText',
      },
      {
        title: 'Action',
        dataIndex: 'action',
        align: 'center',
        width: '120px',
      },
    ]

    const displayedImagesData = computed(() => {
      const keyword = appliedImageSearchText.value.trim().toLowerCase()
      return imagesData
        .filter((item) => {
          if (!keyword) return true
          return getImageAltText(item).toLowerCase().includes(keyword)
        })
        .sort((a, b) => {
          const aTime = a.createdTime ? new Date(a.createdTime).getTime() : 0
          const bTime = b.createdTime ? new Date(b.createdTime).getTime() : 0
          return bTime - aTime
        })
        .map(item => ({ ...item, key: item.id }))
    })

    const onSearchImages = () => {
      appliedImageSearchText.value = imageSearchText.value
      imagePagination.value.current = 1
    }

    watch(imageSearchText, (val) => {
      if (val) return
      appliedImageSearchText.value = ''
      imagePagination.value.current = 1
    })

    const onImageTableChange = ({ current, pageSize }) => {
      imagePagination.value.current = current
      imagePagination.value.pageSize = pageSize
    }

    const onSelectImage = ({ imageUrl, fileName, altText }) => {
      console.log('onSelectImage', { imageUrl, fileName, altText })
      imageState[imageState.activeTab].imageUrl = imageUrl
      imageState.internal.altText = altText || fileName
    }

    const getImageRowClassName = record => (
      imageState[imageState.activeTab].imageUrl === getImageDisplayUrl(record)
        ? 'image-table-row-selected'
        : ''
    )

    const getImageTableCustomRow = record => ({
      onClick: () => {
        if (editableImageData[record.id]) return
        onSelectImage({
          id: record.id,
          imageUrl: getImageDisplayUrl(record),
          fileName: record.fileName,
          altText: getImageAltText(record),
        })
      },
    })

    const onEditImage = (record) => {
      editableImageData[record.id] = {
        altText: getImageAltText(record),
      }
    }

    const onCancelImageEdit = (record) => {
      delete editableImageData[record.id]
    }

    const onSaveImageAlt = async (record) => {
      const altText = (editableImageData[record.id]?.altText || '').trim()
      const { data, message } = await updateManagementItem({
        entityType: 'image',
        id: record.id,
        altText,
      })

      if (!data) {
        antMessage.error(`Failed to update image alt text: ${message}`)
        return
      }

      const item = imagesData.find(f => f.id === record.id)
      if (item) item.altText = altText
      if (imageState.internal.imageUrl === getImageDisplayUrl(record)) {
        imageState.internal.altText = altText
      }
      delete editableImageData[record.id]
    }

    const onPreviewImage = (url) => {
      imageState.previewUrl = url
      imageState.previewVisible = true
    }

    const removeImageFromListById = (targetId) => {
      const idx = imagesData.findIndex(f => f.id === targetId)
      if (idx >= 0) imagesData.splice(idx, 1)
    }

    const onDeleteImage = async (id) => {
      console.log('onDeleteImage', id)
      const item = imagesData.find(f => f.id === id)
      const fileName = getImageFileName(item, id)

      const { data, message } = await deleteImages([fileName])
      if (!data) {
        antMessage.error(`Failed to delete image: ${message}`)
        return
      }

      const { data: managementItemData, message: managementItemMessage } = await deleteManagementItem({
        entityType: 'image',
        id: fileName,
      })
      if (!managementItemData) {
        antMessage.error(`Failed to delete image management item: ${managementItemMessage || 'unknown error'}`)
        return
      }

      removeImageFromListById(id);
      [...imagesData].filter(f => f.fileName === fileName).forEach((f) => {
        removeImageFromListById(f.id)
      })

      if (imageState.internal.imageUrl === getImageDisplayUrl(item)) {
        imageState.internal.imageUrl = ''
        imageState.internal.altText = ''
      }
    }

    const closeImageModal = () => {
      Object.assign(imageState, { ...defaultImageState })
      imageSearchText.value = ''
      appliedImageSearchText.value = ''
      imagePagination.value.current = 1
      Object.keys(editableImageData).forEach((key) => {
        delete editableImageData[key]
      })
    }

    const onUpload = async (info) => {
      imageState.uploadLoading = true

      console.log('onUpload info', info)
      const { file, onSuccess, onError } = info
      const formData = new FormData()
      const createdTime = moment().utcOffset(8).toISOString(true)
      formData.append('image', file)
      formData.append('createdTime', createdTime)

      const { data, error, message } = await uploadImage(formData)
      imageState.uploadLoading = false

      if (data) {
        imagesData.unshift({
          id: data.fileName,
          imageUrl: data.s3Url,
          fileName: data.fileName,
          altText: data.fileName,
          createdTime: data.createdTime || createdTime,
        })
        onSelectImage({
          imageUrl: data.s3Url,
          fileName: data.fileName,
          altText: data.fileName,
        })
        onSuccess(data)
      } else {
        antMessage.error(`Failed to upload image: ${message}`)
        onError(error)
      }
    }

    const beforeUpload = (file) => {
      console.log('beforeUpload file', file)
      const fileTypeLimit = file.type === 'image/jpeg' || file.type === 'image/png' || file.type === 'image/svg+xml'
      if (!fileTypeLimit) antMessage.error('Only support JPG, PNG and SVG')

      const fileSizeLimit = file.size / 1024 / 1024 < 1
      if (!fileSizeLimit) antMessage.error('Image size should not over 1mb')

      return (fileTypeLimit && fileSizeLimit) || AntUpload.LIST_IGNORE
    }

    const onClickLinkButton = () => {
      const { from, to } = editor.value.state.selection
      const selectedText = editor.value.state.doc.textBetween(from, to, ' ')

      console.log('onClickLinkButton', 'editor.value.state.doc', editor.value.state.doc, 'editor.value.state.selection', editor.value.state.selection)
      const marks = editor.value.state.selection.$anchor.marks()
      const linkMark = marks.find(mark => mark.type.name === 'link')
      const selectedLinkUrl = linkMark?.attrs?.href || ''

      console.log('onClickLinkButton', {
        marks, linkMark, selectedText, selectedLinkUrl,
      })

      linkState.activeTab = 'internal'

      if (selectedLinkUrl) {
        const matchInternalLink = selectedLinkUrl.match(/(\w{10})(?:_(encn|en|cn))?$/)
        if (matchInternalLink) {
          const [, id, lang = 'encn'] = matchInternalLink
          console.log('matchInternalLink', matchInternalLink)
          linkState.internal.text = selectedText
          linkState.internal.url = `${id}_${lang}`
          linkState.internal.lang = lang
          nextTick(() => {
            const el = document.querySelector(`.section-editor:not(.viewer) #section-${id}`)
            if (el) {
              el.click()
              el.scrollIntoView({ behavior: 'smooth', block: 'center' })
            }
          })
        }
      }

      linkState.modalVisible = true
    }

    // watchEffect(() => {
    //   if (!editor?.value) return

    //   const { selection } = editor.value.state
    //   const { from } = selection
    //   const { to } = selection

    //   // 獲取 DOM 中的游標位置
    //   const domSelection = window.getSelection()
    //   const domRange = domSelection?.rangeCount > 0 ? domSelection.getRangeAt(0) : null
    //   const domOffset = domRange?.startOffset || 0
    //   const domNode = domRange?.startContainer || null

    //   // 獲取 ProseMirror 的 DOM 座標
    //   const coords = editor.value.view.coordsAtPos(from)

    //   console.log('游標位置調試信息:', {
    //     // ProseMirror 位置
    //     prosemirrorFrom: from,
    //     prosemirrorTo: to,
    //     prosemirrorCoords: coords,

    //     // DOM 位置
    //     domOffset,
    //     domNode,
    //     domNodeType: domNode?.nodeType,
    //     domNodeText: domNode?.textContent?.substring(0, 50),

    //     // 選中的文本
    //     selectedText: editor.value.state.doc.textBetween(from, to, ' '),

    //     // 當前節點信息
    //     currentNode: selection.$anchor.parent.type.name,
    //     currentPos: selection.$anchor.pos,
    //     currentOffset: selection.$anchor.offset,
    //   })
    // })

    const insertLink = () => {
      if (linkState[linkState.activeTab].url) {
        const text = linkState[linkState.activeTab].text || linkState[linkState.activeTab].url

        // 將數字和點之間的空白替換為零寬度空格來防止 1. 2. 3. 被轉成 ordered list
        const escapedText = text.replace(/(\d+)\./g, '$1\u200B.')

        editor.value.chain().focus()
          .insertContent(escapedText)
          .command(({ tr }) => {
            // 選中剛剛插入的文本
            const pos = editor.value.state.selection.from
            console.log('insertLink pos', {
              length: escapedText.length,
              pos,
              stateFrom: editor.value.state.selection.from,
              trFrom: tr.selection.from,
              stateSelection: editor.value.state.selection,
              trSelection: tr.selection,
            })
            tr.setSelection(TextSelection.create(tr.doc, pos, tr.selection.from))
            return true
          })
          .setLink({
            rel: null,
            href: linkState[linkState.activeTab].url,
          })
          .run()
      }
      closeLinkModal()
    }

    const insertImage = () => {
      if (imageState[imageState.activeTab].imageUrl) {
        editor.value.chain().focus().setImage({
          src: imageState[imageState.activeTab].imageUrl,
          alt: imageState[imageState.activeTab].altText,
        }).run()
      }
      closeImageModal()
    }

    const calculateMenuPosition = (clientX, clientY) => {
      const groupConfigs = [
        {
          title: 'Cell Actions',
          items: [
            { condition: editor.value?.can().mergeCells(), name: 'mergeCells' },
            { condition: editor.value?.can().splitCell(), name: 'splitCell' },
          ],
        },
        {
          title: 'Column Actions',
          items: [
            { condition: editor.value?.can().addColumnBefore(), name: 'addColumnBefore' },
            { condition: editor.value?.can().addColumnAfter(), name: 'addColumnAfter' },
            { condition: editor.value?.can().deleteColumn(), name: 'deleteColumn' },
          ],
        },
        {
          title: 'Row Actions',
          items: [
            { condition: editor.value?.can().addRowBefore(), name: 'addRowBefore' },
            { condition: editor.value?.can().addRowAfter(), name: 'addRowAfter' },
            { condition: editor.value?.can().deleteRow(), name: 'deleteRow' },
          ],
        },
        {
          title: 'Table Actions',
          items: [
            { condition: editor.value?.can().deleteTable(), name: 'deleteTable' },
          ],
        },
      ]

      const visibleGroups = groupConfigs.filter(group => group.items.some(item => item.condition))
      const totalVisibleItems = visibleGroups.reduce((acc, group) => acc + group.items.filter(item => item.condition).length, 0)

      const menuItemHeight = 32
      const menuTitleHeight = 24
      const menuPadding = 16
      const menuBorder = 2
      const menuWidth = 200

      const totalHeight = visibleGroups.length * menuTitleHeight
        + totalVisibleItems * menuItemHeight
        + menuPadding + menuBorder

      // 如果沒有可見項目，設置最小高度
      const finalHeight = Math.max(totalHeight, 50)

      const windowHeight = window.innerHeight
      const windowWidth = window.innerWidth

      // 調整 Y 位置：如果選單會超出底部，則向上調整位置
      let adjustedY = clientY
      if (clientY + finalHeight > windowHeight) adjustedY = clientY - finalHeight
      if (adjustedY < 0) adjustedY = 10 // 確保選單不會超出頂部

      // 調整 X 位置：如果選單會超出右邊界，則向左調整位置
      let adjustedX = clientX
      if (clientX + menuWidth > windowWidth) adjustedX = clientX - menuWidth
      if (adjustedX < 0) adjustedX = 10 // 確保選單不會超出左邊界

      return { x: adjustedX, y: adjustedY }
    }

    const onContextMenu = (e) => {
      if (isPreviewMode.value) return
      e.stopPropagation()

      const isInTable = editor.value.isActive('table')
      if (!isInTable) return

      // 在表格中，阻止默認行為並顯示自定義選單
      e.preventDefault()

      const position = calculateMenuPosition(e.clientX, e.clientY)
      tableContextMenuState.visible = true
      tableContextMenuState.x = position.x
      tableContextMenuState.y = position.y
      console.log('tableContextMenuState', tableContextMenuState)
    }

    const handleMergeCells = () => {
      editor.value.chain().focus().mergeOrSplit().run()
      tableContextMenuState.visible = false
    }

    const handleSplitCells = () => {
      editor.value.chain().focus().splitCell().run()
      tableContextMenuState.visible = false
    }

    const handleAddColumnBefore = () => {
      editor.value.chain().focus().addColumnBefore().run()
      tableContextMenuState.visible = false
    }

    const handleAddColumnAfter = () => {
      editor.value.chain().focus().addColumnAfter().run()
      tableContextMenuState.visible = false
    }

    const handleDeleteColumn = () => {
      editor.value.chain().focus().deleteColumn().run()
      tableContextMenuState.visible = false
    }

    const handleAddRowBefore = () => {
      editor.value.chain().focus().addRowBefore().run()
      tableContextMenuState.visible = false
    }

    const handleAddRowAfter = () => {
      editor.value.chain().focus().addRowAfter().run()
      tableContextMenuState.visible = false
    }

    const handleDeleteRow = () => {
      editor.value.chain().focus().deleteRow().run()
      tableContextMenuState.visible = false
    }

    const handleDeleteTable = () => {
      editor.value.chain().focus().deleteTable().run()
      tableContextMenuState.visible = false
    }

    const closeTableContextMenu = () => {
      // 如果點擊的是菜單本身或菜單不可見，不關閉
      if (!tableContextMenuState.visible) {
        return
      }
      tableContextMenuState.visible = false
    }

    const getModalContainer = () => editorRef.value

    const getPopconfirmContainer = () => document.body

    const headingMenuVisible = ref(false)

    const toggleHeadingMenu = () => {
      headingMenuVisible.value = !headingMenuVisible.value

      // 如果選單要顯示，檢查是否需要調整位置
      if (headingMenuVisible.value) {
        nextTick(() => {
          const headingMenu = document.querySelector('.heading-menu')
          if (headingMenu) {
            const rect = headingMenu.getBoundingClientRect()
            const windowHeight = window.innerHeight

            // 如果選單超出底部，向上調整位置
            if (rect.bottom > windowHeight) {
              headingMenu.style.top = 'auto'
              headingMenu.style.bottom = '100%'
            }
          }
        })
      }
    }

    const handleHeadingClick = (level) => {
      editor.value.chain().focus().toggleHeading({ level }).run()
      headingMenuVisible.value = false
    }

    const closeHeadingMenu = (e) => {
      // 如果點擊的是菜單本身、heading button 或菜單不可見，不關閉
      if (e.target.closest('.heading-menu') || e.target.closest('#heading') || !headingMenuVisible.value) {
        return
      }
      headingMenuVisible.value = false
    }

    const defaultToolbarItems = computed(() => (editor.value ? [
      [
        {
          name: 'heading',
          title: 'Heading',
          icon: 'HeadingIcon',
          isActive: false,
          disabled: false,
          onClick: toggleHeadingMenu,
        },
        {
          name: 'code',
          title: 'Code',
          icon: 'CodeIcon',
          isActive: editor.value.isActive('code'),
          disabled: false,
          onClick: () => editor.value.chain().focus().toggleCode().run(),
        },
      ],
      [
        {
          name: 'bold',
          title: 'Bold',
          icon: 'BoldIcon',
          isActive: editor.value.isActive('bold'),
          disabled: false,
          onClick: () => editor.value.chain().focus().toggleBold().run(),
        },
        {
          name: 'italic',
          title: 'Italic',
          icon: 'ItalicIcon',
          isActive: editor.value.isActive('italic'),
          disabled: false,
          onClick: () => editor.value.chain().focus().toggleItalic().run(),
        },
        {
          name: 'strikethrough',
          title: 'Strikethrough',
          icon: 'StrikethroughIcon',
          isActive: editor.value.isActive('strike'),
          disabled: false,
          onClick: () => editor.value.chain().focus().toggleStrike().run(),
        },
        {
          name: 'color',
          title: 'Red Text Color',
          icon: 'ColorIcon',
          isActive: editor.value.isActive('textStyle', { color: '#ff0000' }),
          onClick: () => {
            const red = '#ff0000'
            if (editor.value.isActive('textStyle', { color: red })) {
              editor.value.chain().focus().unsetColor().run()
            } else {
              editor.value.chain().focus().setColor(red).run()
            }
          },
        },
      ],
      [
        {
          name: 'bulletList',
          title: 'Bullet List',
          icon: 'ListIcon',
          isActive: editor.value.isActive('bulletList'),
          disabled: false,
          onClick: () => editor.value.chain().focus().toggleBulletList().run(),
        },
        {
          name: 'orderedList',
          title: 'Ordered List',
          icon: 'OrderedListIcon',
          isActive: editor.value.isActive('orderedList'),
          disabled: false,
          onClick: () => editor.value.chain().focus().toggleOrderedList().run(),
        },
      ],
      [
        {
          name: 'table',
          title: 'Table',
          icon: 'TableIcon',
          isActive: false,
          disabled: false,
          onClick: () => editor.value.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run(),
        },
        {
          name: 'image',
          title: 'Image',
          icon: 'ImageIcon',
          isActive: false,
          disabled: false,
          onClick: () => imageState.modalVisible = true,
        },
        {
          name: 'link',
          title: 'Link',
          icon: 'LinkIcon',
          isActive: false,
          disabled: false,
          onClick: onClickLinkButton,
        },
      ],
      [
        {
          name: 'undo',
          title: 'Undo',
          icon: 'UndoIcon',
          isActive: false,
          disabled: !editor.value.can().undo(),
          onClick: () => editor.value.chain().focus().undo().run(),
        },
        {
          name: 'redo',
          title: 'Redo',
          icon: 'RedoIcon',
          isActive: false,
          disabled: !editor.value.can().redo(),
          onClick: () => editor.value.chain().focus().redo().run(),
        },
      ],
    ] : []))

    const toolbarItems = computed(() => defaultToolbarItems.value.map(group => group.filter(({ name }) => !props.removedToolbarItems.includes(name))).filter(group => group.length > 0))

    const handleClickOutsideEditor = (e) => {
      e.stopPropagation()
      if (props.readOnly) return
      if (!editorRef.value) return

      const clickedOnEditor = editorRef.value.contains(e.target) || editorRef.value === e.target
      // console.log('handleClickOutsideEditor clickedOnEditor', clickedOnEditor, e, e.target)

      if (props.previewMode && !clickedOnEditor && !isPreviewMode.value) {
        isPreviewMode.value = true
      } else if (clickedOnEditor && isPreviewMode.value) {
        isPreviewMode.value = false
      }

      if (!clickedOnEditor && props.editorContent !== content.value) {
        pushContentToParent(formatContent(content.value))
      }
    }

    onMounted(() => {
      document.addEventListener('click', handleClickOutsideEditor)
      document.addEventListener('click', closeTableContextMenu)
      document.addEventListener('contextmenu', closeTableContextMenu)
      document.addEventListener('click', closeHeadingMenu)
    })

    onBeforeUnmount(() => {
      throttledUpdate.cancel()

      document.removeEventListener('click', handleClickOutsideEditor)
      document.removeEventListener('click', closeTableContextMenu)
      document.removeEventListener('contextmenu', closeTableContextMenu)
      document.removeEventListener('click', closeHeadingMenu)
    })

    return {
      isDev,
      editor,
      editorRef,
      content,
      contentHtml,
      isPreviewMode,
      linkState,
      closeLinkModal,
      insertLink,
      onClickLinkButton,
      linkTreeDocId,
      treeData,
      displayedTreeData,
      expandedKeys,
      selectedKeys,
      treeSearchState,
      onSelectTree,
      onSearchTree,
      imageState,
      getModalContainer,
      getPopconfirmContainer,
      imagesData,
      imageSearchText,
      imageTableColumns,
      displayedImagesData,
      imagePagination,
      editableImageData,
      getImageAltText,
      getImageRowClassName,
      getImageTableCustomRow,
      onSearchImages,
      onImageTableChange,
      onSelectImage,
      onEditImage,
      onCancelImageEdit,
      onSaveImageAlt,
      onPreviewImage,
      getImageDisplayUrl,
      onDeleteImage,
      closeImageModal,
      insertImage,
      onUpload,
      beforeUpload,
      tableContextMenuState,
      onContextMenu,
      handleMergeCells,
      handleSplitCells,
      handleAddColumnBefore,
      handleAddColumnAfter,
      handleDeleteColumn,
      handleAddRowBefore,
      handleAddRowAfter,
      handleDeleteRow,
      handleDeleteTable,
      headingMenuVisible,
      toggleHeadingMenu,
      handleHeadingClick,
      toolbarItems,
      handleDisableLinkClick,
    }
  },
}
</script>

<style scoped lang="less">
.section-editor-container {
  display: flex;
  flex-direction: column;
  height: v-bind(height);
}

.modal-container {
  display: flex;
  flex-direction: column;
  height: 670px;
}

.modal-tab-pane {
  display: flex;
  flex-direction: column;
}
.modal-scroll-container {
  display: flex;
  flex-direction: column;
  overflow-y: auto;
}

.form-item {
  margin-bottom: 15px;
  display: flex;
  align-items: center;
}

.form-item > label {
  display: block;
  margin-right: 16px;
  flex-shrink: 0;
}

.image-search-row {
  margin-bottom: 16px;
}

.image-table-actions .anticon {
  cursor: pointer;
  margin: 0 6px;
  font-size: 16px;
}

.image-table-actions .anticon:hover {
  color: #1890ff;
}

:deep(.image-management-table .image-table-row-selected) > td {
  background: #e6f7ff;
}

:deep(.image-management-table .ant-table-tbody > tr) {
  cursor: pointer;
}

:deep(.image-management-table .image-preview-cell) {
  vertical-align: middle;
}

.image-preview-wrap {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 48px;
  cursor: zoom-in;
}

:deep(.image-preview-wrap .ant-image) {
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

:deep(.image-preview-wrap .ant-image img) {
  width: 100%;
  height: 100%;
  object-fit: contain;
  object-position: center;
}

:deep(.image-preview-wrap .ant-image-mask) {
  display: none;
}

.editor-toolbar {
  border-bottom: 1px solid #d9d9d9;
  padding: 8px;
  display: flex;
  gap: 8px;
  background: #fff;
  position: relative;
}

.section-editor .toolbar-group {
  display: flex;
  gap: 4px;
  border-right: 1px solid #d9d9d9;
  padding-right: 8px;

  &:not(:has(+ .toolbar-group)) { /* &:last-of-type not work due to .context-menu */
    border-right: none;
    padding-right: 0;
  }
}

.editor-toolbar .ant-btn {
  padding: 4px 8px;
  height: auto;
}

.editor-toolbar .ant-btn[disabled] {
  color: rgba(0, 0, 0, 0.25);
  background: transparent;
  border-color: transparent;
  text-shadow: none;
  box-shadow: none;
}

.context-menu {
  position: fixed;
  background: white;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  min-width: 160px;
}

.context-menu-group {
  padding: 4px 0;
  border-bottom: 1px solid #f0f0f0;
}

.context-menu-group:not(:has(.context-menu-item:not([style*="display: none"]))) {
  display: none;
}

.context-menu-group:last-child {
  border-bottom: none;
}

.context-menu-title {
  padding: 4px 12px;
  color: rgba(0, 0, 0, 0.45);
  font-size: 12px;
}

.context-menu-item {
  padding: 8px 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
}

.context-menu-item.active {
  background-color: #bae7ff;
}

.context-menu-item:hover {
  background-color: #f5f5f5;
}

.context-menu-item.disabled {
  color: rgba(0, 0, 0, 0.25);
  cursor: not-allowed;
}

.context-menu-item.disabled:hover {
  background-color: transparent;
}

.heading-menu {
  position: absolute;
  top: 100%;
  left: 0;
}
</style>

<style lang="less">
.section-editor .modal-container .ant-tabs-content {
  height: 100%;
}

.section-editor .ant-upload-picture-card-wrapper {
  width: auto;
}

.section-editor .ant-upload.ant-upload-select-picture-card > .ant-upload {
  width: 100px;
  height: 100px;
}

.section-editor .ant-upload.ant-upload-select-picture-card {
  width: 100px;
  height: 100px;
  margin: 0;
}

.section-editor {
  font-family: Roboto, sans-serif;
  line-height: 1.5em;
  color: #333333;
  outline: none;
  display: flex;
  flex-direction: column;
  // height: auto;
  &:not(.viewer) {
    height: 100%;
    flex-grow: 1;
    flex-shrink: 1;
    border: 1px solid #d9d9d9;
    border-radius: 2px;
    background-color: #fff;
  }
}

/* .section-editor:not(.preview-mode) {
  min-height: v-bind(height);
}*/

.section-editor.preview-mode {
  border: 1px solid rgba(255, 255, 255, 0);
  transition: all 0.3s;
  background-color: inherit;
  &:hover {
  cursor: pointer;
  border: 1px solid #d9d9d9;
  background-color: #fff;
  }
}

.section-editor.preview-mode.read-only {
  cursor: default;
  background-color: #f5f5f5;
  border: 1px solid #d9d9d9;
  &:hover {
    cursor: default;
    background-color: #f5f5f5;
    border: 1px solid #d9d9d9;
  }
}

.section-editor-content {
  padding: 16px;
  flex: 1;
}

.section-editor:not(.preview-mode) .section-editor-content {
  overflow-y: auto;
}

.preview-mode .section-editor-content {
  padding: 8px;
  max-height: 300px;
  min-height: 32px;
  overflow-y: auto;
}

.preview-mode.read-only .section-editor-content {
  max-height: none;
  cursor: not-allowed;
}

.section-editor-content .tiptap {
  height: 100%;
}

.section-editor .ProseMirror-focused {
  outline: none;
}

.section-editor a, .section-editor a:visited {
  text-decoration: none;
  color: #947364;
}

.section-editor img {
  max-width: 100%;
  align-self: flex-start;
}

.section-editor hr {
  width: 100%;
}

.section-editor table {
  overflow: auto;
  border-collapse: collapse;
  margin-top: 1.5em;
  margin-bottom: 1.5em;
}

.section-editor table tr {
  background-color: #fff;
  &:nth-child(2n) {
    background-color: #fafafa;
  }
}

.section-editor table td,
.section-editor table th {
  padding: 6px 13px;
  border: 1px solid rgb(221, 221, 221);
  position: relative;
}

.section-editor table th {
  font-weight: bold;
  text-align: left;
}

.section-editor table .selectedCell:after {
  background: rgba(200, 200, 255, 0.4);
  content: '';
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  pointer-events: none;
  position: absolute;
  z-index: 2;
}

.section-editor table p:first-child {
  margin-top: 0;
}

.section-editor .anticon {
  font-size: 16px;
}

.section-editor code {
  color: #e53935;
  background-color: rgba(38, 50, 56, 0.05);
  font-family: Menlo, Monaco, Consolas, "Courier New", monospace;
  border-radius: 2px;
  border: 1px solid rgba(38, 50, 56, 0.1);
  padding: 0px 5px;
  white-space: normal;
  word-break: normal;
  overflow-wrap: normal;
}

.section-editor ul, .section-editor ol {
  padding-left: 2em;
  margin: 0px 0px 1em;
}

.section-editor .context-menu-item {
  h3, h4 {
    margin: 0;
  }
}

.section-editor h3 {
  font-weight: bold;
  font-size: 16px;
  margin: 14px 0;
}

.section-editor h4 {
  font-weight: bold;
  font-size: 14px;
  margin: 16px 0;
}

.section-editor #color .anticon {
  color: #ff0000;
}

.section-editor p {
  margin: 14px 0;
  &:first-child {
    margin-top: 0px;
  }
  &:last-child {
    margin-bottom: 0px;
  }
  &:empty {
    display: none;
  }
}

.section-editor ul li p {
  margin: 4px;
}
</style>
